import React, { FC, ReactElement, useRef } from 'react'
import { useClickAway } from 'react-use'
import { Box, makeStyles, createStyles, Typography } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'

const useFilterBaseStyles = makeStyles(theme => {
  return createStyles<
    'renderEdit' | 'renderDisplay' | 'label' | 'container' | 'containerBackground',
    { large: boolean; alignRight: boolean; isSelected: boolean }
  >({
    renderEdit: {
      display: props => (props.isSelected ? '' : 'none'),
      zIndex: 99999,
      right: props => (props.alignRight ? 0 : ''),
      width: props => (props.large ? 382 : 332),
      top: -10,
      position: 'absolute',
      padding: theme.spacing(3),
      background: theme.palette.background.default,
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[10],

      [theme.breakpoints.down('lg')]: {
        width: '100% !important',
        left: 0,
      },
    },
    renderDisplay: {
      borderRadius: theme.shape.borderRadius,
    },
    label: {
      color: theme.palette.grey[600],
      fontSize: theme.typography.caption.fontSize,
    },
    container: {
      flexDirection: 'row',
      width: '100%',
      borderRadius: 8,
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      paddingRight: theme.spacing(1),
      paddingLeft: theme.spacing(2),
      '&:hover, &:focus': {
        backgroundColor: brandColors.coolGray1,
        cursor: 'pointer',
      },
    },
    containerBackground: {
      [theme.breakpoints.down('lg')]: {
        opacity: 0.5,
        zIndex: 9,
        top: 0,
        left: 0,
        background: brandColors.coolGray5,
        position: 'fixed',
        height: '100%',
        width: '100%',
      },
    },
  })
})

export interface IFilterBaseProps {
  /**
   * Display edit mode
   */
  isSelected: boolean
  onOpen: () => void
  onClose: () => void
  /**
   * Returns the block of React JSX for when display is active
   */
  renderDisplay: ReactElement
  /**
   * Returns the block of React JSX for when edit is active
   */
  renderEdit: ReactElement
  /**
   * Label for display and edit views
   */
  label?: string
  /**
   * Set width of renderEdit component
   */
  large?: boolean
  /**
   * Set direction of renderEdit component
   */
  alignRight?: boolean
  /**
   * Mount/unmount the edit mode component
   * @default true
   */
  isRenderEditMounted?: boolean
  /**
   * Triggers the onClick event handler when the user clicks outside of the component
   * @default true
   */
  useClickAway?: boolean

  dataTest?: string
}

export const FilterBase: FC<IFilterBaseProps> = ({
  alignRight = false,
  dataTest,
  isRenderEditMounted = true,
  isSelected = false,
  label: labelProp,
  large = false,
  onClose,
  onOpen,
  renderDisplay,
  renderEdit,
  useClickAway: useClickAwayProp = true,
}): ReactElement => {
  const ref = useRef<HTMLDivElement>(null)

  /**
   * When user clicks away from it
   */
  useClickAway(ref, () => {
    if (useClickAwayProp && isSelected) {
      onClose()
    }
  })

  const classes = useFilterBaseStyles({ large, alignRight, isSelected })

  return (
    <>
      {isSelected && <Box className={classes.containerBackground} />}
      <Box data-test='filter-base'>
        {isRenderEditMounted && (
          <Box data-test='render-edit' className={classes.renderEdit}>
            <div ref={ref}>{renderEdit}</div>
          </Box>
        )}
        <Box data-test={dataTest} onClick={onOpen} className={classes.renderDisplay}>
          <Box className={classes.container}>
            <Typography variant='caption'>{labelProp}</Typography>
            {renderDisplay}
          </Box>
        </Box>
      </Box>
    </>
  )
}
